import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Package, Plus, Minus, Loader2 } from "lucide-react";
import { API, R2_URL } from "@/lib/api";
import { productImgUrl } from "@/lib/imageUtils";
import { useSEO } from "@/hooks/useSEO";
import { useCart } from "@/context/CartContext";

export default function ProductDetailPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImg, setSelectedImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  useSEO(
    product?.meta_title || product?.name,
    product?.meta_description || (product ? `${product.name} - ${product.price_vat?.toFixed(2)} Kc s DPH. ${product.quantity > 0 ? 'Skladem' : 'Na dotaz'}. Kod: ${product.reference || '-'}.` : '')
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/products/${productId}`),
      axios.get(`${API}/products/${productId}/images`)
    ]).then(([prodRes, imgRes]) => {
      setProduct(prodRes.data);
      setImages(imgRes.data.images || []);
      setSelectedImg(0);
    }).catch(console.error).finally(() => setLoading(false));
  }, [productId]);

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>;
  if (!product) return <div className="text-center py-12 text-slate-500">Produkt nenalezen</div>;

  const inStock = product.quantity > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="product-detail-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": product.description_short?.replace(/<[^>]*>/g, '') || product.name,
        "sku": product.reference || undefined,
        "gtin13": product.ean13 || undefined,
        "image": productImgUrl(product, 'thickbox') || undefined,
        "brand": product.manufacturer_name ? { "@type": "Brand", "name": product.manufacturer_name } : undefined,
        "offers": {
          "@type": "Offer",
          "url": window.location.href,
          "priceCurrency": "CZK",
          "price": product.price_vat?.toFixed(2),
          "priceValidUntil": new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
          "availability": inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "seller": { "@type": "Organization", "name": "Reguli s.r.o." }
        }
      }) }} />
      <nav className="text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-[#004E98]">Uvod</Link>
        <span className="mx-2">/</span>
        <Link to="/produkty" className="hover:text-[#004E98]">Produkty</Link>
        {product.category_names?.[0] && (<><span className="mx-2">/</span><span>{product.category_names[0]}</span></>)}
      </nav>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="aspect-square bg-slate-50 rounded-md flex items-center justify-center overflow-hidden border border-slate-200">
            {images.length > 0 ? (
              <img src={`${R2_URL}/p/${images[selectedImg]?.path}`} alt={images[selectedImg]?.legend || product.name} className="w-full h-full object-contain p-4" data-testid="product-main-image" onError={(e) => { e.target.style.display='none'; }} />
            ) : productImgUrl(product, 'thickbox') ? (
              <img src={productImgUrl(product, 'thickbox')} alt={product.name} className="w-full h-full object-contain p-4" data-testid="product-main-image" onError={(e) => { e.target.style.display='none'; }} />
            ) : (
              <Package className="w-32 h-32 text-slate-300" />
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
              {images.map((img, i) => (
                <button key={img.id_image} onClick={() => setSelectedImg(i)} className={`w-16 h-16 flex-shrink-0 rounded border-2 overflow-hidden ${i === selectedImg ? 'border-[#004E98]' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`thumbnail-${i}`}>
                  <img src={`${R2_URL}/p/${img.sizes?.small || img.path}`} alt="" className="w-full h-full object-contain" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">{product.name}</h1>
          <div className="flex items-center gap-4 mb-4">
            {product.reference && <span className="text-sm text-slate-500">Kod: {product.reference}</span>}
            {product.ean13 && <span className="text-sm text-slate-500">EAN: {product.ean13}</span>}
          </div>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm mb-6 ${inStock ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            <span className={`w-2 h-2 rounded-full ${inStock ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
            {inStock ? `Skladem (${product.quantity} ks)` : 'Na dotaz'}
          </div>
          <div className="bg-slate-50 rounded-md p-6 mb-6">
            <div className="text-3xl font-bold text-[#004E98] mb-1">{product.price_vat?.toFixed(2)} Kc</div>
            <div className="text-sm text-slate-500">{product.price?.toFixed(2)} Kc bez DPH</div>
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-slate-300 rounded-sm">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-slate-50"><Minus className="w-4 h-4" /></button>
              <input type="number" value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 h-10 text-center border-x border-slate-300 outline-none" data-testid="quantity-input" />
              <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 flex items-center justify-center hover:bg-slate-50"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => addItem(product, quantity)} className="flex-1 bg-[#FF6B35] text-white hover:bg-[#E85D2A] rounded-sm px-6 py-3 font-medium uppercase tracking-wide text-sm transition-all flex items-center justify-center gap-2" data-testid="add-to-cart-button">
              <ShoppingCart className="w-5 h-5" /> Pridat do kosiku
            </button>
          </div>
          <div className="border-t border-slate-200 pt-6">
            {product.manufacturer_name && (<div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Vyrobce</span><span className="font-medium">{product.manufacturer_name}</span></div>)}
            {product.weight > 0 && (<div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Hmotnost</span><span className="font-medium">{product.weight} kg</span></div>)}
            {product.category_names?.length > 0 && (<div className="flex justify-between py-2"><span className="text-slate-500">Kategorie</span><span className="font-medium text-right">{product.category_names.slice(0, 3).join(', ')}</span></div>)}
          </div>
        </div>
      </div>

      {(product.description || product.description_short) && (
        <div className="mt-12">
          <h2 className="text-xl font-bold text-slate-800 mb-4">Popis produktu</h2>
          <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: product.description || product.description_short }} />
        </div>
      )}
    </div>
  );
}
